import axios from 'axios';
import {
  CREATE_USER,
  UPDATE_USER,
  get_request_config,
} from '@/utils/system_constants/constant.js';
import {
  create_form_defaults,
  has_valid_name,
  is_coordinator_role,
  is_valid_email,
  is_valid_phone_number,
  parse_api_data,
  parse_error_message,
} from './user_management_helpers';

export default {
  parse_error_message(error, fallback) {
    return parse_error_message(error, fallback);
  },

  reset_create_form() {
    this.create_form = create_form_defaults(this.roles[0]?.role || '');
  },

  open_create_user_modal() {
    if (!this.roles.length) {
      this.fetch_roles();
    }

    if (is_coordinator_role(this.create_form.role) && !this.entity_type_options.length) {
      this.fetch_entity_type_options();
    }
  },

  open_edit_user_modal(user) {
    this.edit_form = {
      user_id: user.id,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      phone_number: user.phone_number || '',
      role_id: user.role_id || '',
      entity_type_id: user.entity_type_id ? String(user.entity_type_id) : '',
      entity_type_name: user.entity_type_name || '',
    };

    if (!this.roles.length) {
      this.fetch_roles();
    }

    if (String(user.role || '').toLowerCase().includes('coordinator')) {
      this.fetch_entity_type_options();
    }
  },

  sanitize_user_name_field(form_name, field_name) {
    const form = this[form_name];
    if (!form) return;

    form[field_name] = String(form[field_name] || '')
      .replace(/[^A-Za-z\s'-]/g, '')
      .slice(0, 50);
  },

  sanitize_phone_field(form_name) {
    const form = this[form_name];
    if (!form) return;

    form.phone_number = String(form.phone_number || '')
      .replace(/\D/g, '')
      .slice(0, 10);
  },

  validate_create_user() {
    if (!this.create_form.first_name.trim()) return 'First name is required.';
    if (!has_valid_name(this.create_form.first_name)) return 'First name must be at least 2 letters and cannot contain numbers.';
    if (!this.create_form.last_name.trim()) return 'Last name is required.';
    if (!has_valid_name(this.create_form.last_name)) return 'Last name must be at least 2 letters and cannot contain numbers.';
    if (!this.create_form.email.trim()) return 'Email is required.';
    if (!is_valid_email(this.create_form.email)) return 'Please enter a valid email address.';
    if (!this.create_form.phone_number.trim()) return 'Phone number is required.';
    if (!is_valid_phone_number(this.create_form.phone_number)) return 'Phone number must be exactly 10 digits.';
    if (!this.create_form.role) return 'Role is required.';
    if (is_coordinator_role(this.create_form.role) && !String(this.create_form.entity_type_id || '').trim()) {
      return 'Entity Type is required for the Coordinator role.';
    }
    return '';
  },

  async create_user() {
    const validation_message = this.validate_create_user();
    if (validation_message) {
      this.show_error(validation_message);
      return;
    }

    this.creating_user = true;
    const loader = await this.show_loader('Creating user...');

    try {
      const payload = {
        first_name: this.create_form.first_name.trim(),
        last_name: this.create_form.last_name.trim(),
        email: this.create_form.email.trim(),
        phone_number: this.create_form.phone_number.trim(),
        role: this.create_form.role,
      };

      const selected_entity_type = this.get_selected_create_entity_type();
      if (is_coordinator_role(this.create_form.role) && selected_entity_type) {
        payload.entity_type_id = selected_entity_type.id;
        if (selected_entity_type.project_id) {
          payload.project_id = selected_entity_type.project_id;
        }
      }

      const response = await axios.post(CREATE_USER, payload, get_request_config());
      const response_data = parse_api_data(response.data);

      if (response_data?.status && response_data.status !== 'success') {
        throw new Error(response_data.message || 'User creation failed.');
      }

      this.close_modal('create_user_modal');
      this.reset_create_form();
      await this.fetch_users();
      this.show_success(response_data.message || 'User created successfully.');
    } catch (error) {
      this.show_error(parse_error_message(error, 'Failed to create user.'));
    } finally {
      this.creating_user = false;
      this.close_loader(loader);
    }
  },

  validate_edit_user() {
    if (!this.edit_form.first_name.trim()) return 'First name is required.';
    if (!has_valid_name(this.edit_form.first_name)) return 'First name must be at least 2 letters and cannot contain numbers.';
    if (!this.edit_form.last_name.trim()) return 'Last name is required.';
    if (!has_valid_name(this.edit_form.last_name)) return 'Last name must be at least 2 letters and cannot contain numbers.';
    if (!this.edit_form.email.trim()) return 'Email is required.';
    if (!is_valid_email(this.edit_form.email)) return 'Please enter a valid email address.';
    if (!this.edit_form.phone_number.trim()) return 'Phone number is required.';
    if (!is_valid_phone_number(this.edit_form.phone_number)) return 'Phone number must be exactly 10 digits.';
    if (!this.edit_form.role_id) return 'Role is required.';
    const selected_role = this.roles.find((role) => String(role.id) === String(this.edit_form.role_id));
    if (is_coordinator_role(selected_role?.role) && !String(this.edit_form.entity_type_id || '').trim()) {
      return 'Entity Type is required for the Coordinator role.';
    }
    return '';
  },

  async update_user() {
    const validation_message = this.validate_edit_user();
    if (validation_message) {
      this.show_error(validation_message);
      return;
    }

    this.updating_user = true;
    const loader = await this.show_loader('Updating user...');

    try {
      const payload = {
        user_id: this.edit_form.user_id,
        first_name: this.edit_form.first_name.trim(),
        last_name: this.edit_form.last_name.trim(),
        email: this.edit_form.email.trim(),
        phone_number: this.edit_form.phone_number.trim(),
        role_id: Number(this.edit_form.role_id),
      };

      const selected_role = this.roles.find((role) => String(role.id) === String(this.edit_form.role_id));
      const selected_entity_type = this.get_selected_edit_entity_type();
      if (is_coordinator_role(selected_role?.role) && selected_entity_type) {
        payload.entity_type_id = selected_entity_type.id;
        if (selected_entity_type.project_id) {
          payload.project_id = selected_entity_type.project_id;
        }
      }

      const response = await axios.post(UPDATE_USER, payload, get_request_config());
      const response_data = parse_api_data(response.data);

      if (response_data?.status && response_data.status !== 'success') {
        throw new Error(response_data.message || 'User update failed.');
      }

      this.close_modal('edit_user_modal');
      await this.fetch_users();
      this.show_success(response_data.message || 'User updated successfully.');
    } catch (error) {
      this.show_error(this.parse_error_message(error, 'Failed to update user.'));
    } finally {
      this.updating_user = false;
      this.close_loader(loader);
    }
  },
};
